import { Locale } from '../constants/languages';

type FormatOptions = {
  value: number;
  locale: string;
  currency: string;
};

type formatNumericValueParams = {
  value: number;
  discount?: number;
  currencyFormat: string;
};

const format = ({ value, locale, currency }: FormatOptions): string => {
  let formattedValue = new Intl.NumberFormat(locale, {
    style : 'currency',
    currency : currency,
    currencyDisplay : 'symbol'
  }).format(value);

  // UYU is the only currency that uses the $ symbol this is needed to change it to $U
  if (currency === 'UYU') {
    formattedValue = formattedValue.replace('$', '$U');
  }

  return formattedValue;
};

const formatNumericValue = ({ value, discount = 0, currencyFormat }: formatNumericValueParams): string => {
  // Extract and format the locale once, clearly
  /*
   * The map already holds canonical BCP-47 tags, so `Intl` takes them as they
   * are. This used to rebuild the tag from the underscore spelling with
   * `locale.split('_')[1].toUpperCase()`, which threw for any currency missing
   * from the map — an unknown currency crashed the price instead of degrading.
   */
  const formattedLocale = Locale[currencyFormat as keyof typeof Locale] ?? Locale.BRL;

  // Calculate the applied discount
  const appliedDiscount = value - (value * discount) / 100;

  // Format the value with the applied discount
  const formattedValue = format({
    value : appliedDiscount,
    locale : formattedLocale,
    currency : currencyFormat,
  });

  return formattedValue;
};

type formatNumericAmountParams = {
  value: number;
  currencyFormat: string;
};

/**
 * The amount alone, punctuated like `formatNumericValue` would (same locale per
 * currency, two decimals) but WITHOUT the currency symbol: `291,38`. For copy
 * that already says what the number is, such as "+ 291,38 de taxa".
 */
export const formatNumericAmount = ({ value, currencyFormat }: formatNumericAmountParams): string => {
  const formattedLocale = Locale[currencyFormat as keyof typeof Locale] ?? Locale.BRL;

  return new Intl.NumberFormat(formattedLocale, {
    minimumFractionDigits : 2,
    maximumFractionDigits : 2,
  }).format(value);
};

export default formatNumericValue;
